import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { Collapse, IconButton, ListItemText } from '@mui/material';

import { Iconify } from '../iconify';


// ----------------------------------------------------------------------

export function RHFSelect({
  name,
  native,
  children,
  slotProps,
  helperText,
  inputProps,
  InputLabelProps,
  ...other
}) {
  const { control } = useFormContext();

  const labelId = `${name}-select-label`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          select
          fullWidth
          SelectProps={{
            native,
            MenuProps: { PaperProps: { sx: { maxHeight: 220, ...slotProps?.paper } } },
            sx: { textTransform: 'capitalize' },
          }}
          InputLabelProps={{ htmlFor: labelId, ...InputLabelProps }}
          inputProps={{ id: labelId, ...inputProps }}
          error={!!error}
          helperText={error ? error?.message : helperText}
          {...other}
        >
          {children}
        </TextField>
      )}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFMultiSelect({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  slotProps,
  helperText,
  ...other
}) {
  const { control } = useFormContext();

  const labelId = `${name}-select-label`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl error={!!error} {...other}>
          {label && (
            <InputLabel htmlFor={labelId} {...slotProps?.inputLabel}>
              {label}
            </InputLabel>
          )}

          <Select
            {...field}
            multiple
            displayEmpty={!!placeholder}
            label={label}
            renderValue={(selected) => {
              const selectedItems = options.filter((item) => selected.includes(item.value));
              if (!selectedItems.length && placeholder) {
                return <Box sx={{ color: 'text.disabled' }}>{placeholder}</Box>;
              }

              if (chip) {
                return (
                  <Box sx={{ gap: 0.5, display: 'flex', flexWrap: 'wrap' }}>
                    {selectedItems.map((item) => (
                      <Chip
                        key={item.value}
                        size="small"
                        variant="soft"
                        label={item.label}
                        {...slotProps?.chip}
                      />
                    ))}
                  </Box>
                );
              }

              return selectedItems.map((item) => item.label).join(', ');
            }}
            {...slotProps?.select}
            inputProps={{ id: labelId, ...slotProps?.select?.inputProps }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {checkbox && (
                  <Checkbox
                    size="small"
                    disableRipple
                    checked={field.value.includes(option.value)}
                    {...slotProps?.checkbox}
                  />
                )}

                {option.label}
              </MenuItem>
            ))}
          </Select>

          {(!!error || helperText) && (
            <FormHelperText error={!!error} {...slotProps?.formHelperText}>
              {error ? error?.message : helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}



// ---------------------------------------------------------------------------

export function RHFMultiSelectNested({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  slotProps,
  helperText,
  ...other
}) {
  const { control } = useFormContext();
  const labelId = `${name}-select-label`;

  // Track expanded nodes for collapsing and expanding
  const [expandedNodes, setExpandedNodes] = useState({});

  // Toggle node expansion
  const toggleNode = (id) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl error={!!error} {...other}>
          {label && (
            <InputLabel htmlFor={labelId} {...slotProps?.inputLabel}>
              {label}
            </InputLabel>
          )}

          <Select
            {...field}
            multiple
            value={field.value || []}
            displayEmpty={!!placeholder}
            label={label}
            renderValue={(selected) => {
              const flattenedOptions = options.flatMap(flattenOptions);

              // Filter out children if their parent is already selected
              const displayItems = flattenedOptions.reduce((acc, item) => {
                const parentSelected = item.parentID && selected.includes(item.parentID);
                if (parentSelected) {
                  return acc; // Skip child if parent is selected
                }



                if (selected.includes(item.id)) {
                  acc.push(item);
                }
                return acc;
              }, []);

              if (!displayItems.length && placeholder) {
                return <Box sx={{ color: 'text.disabled' }}>{placeholder}</Box>;
              }

              if (chip) {
                return (
                  <Box sx={{ gap: 0.5, display: 'flex', flexWrap: 'wrap' }}>
                    {displayItems.map((item) => (
                      <Chip
                        key={item.id}
                        size="small"
                        variant="soft"
                        label={item.name}
                        color='info'
                        {...slotProps?.chip}
                      />
                    ))}
                  </Box>
                );
              }

              return displayItems.map((item) => item.name).join(', ');
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  height: 300,
                  overflowY: 'auto',
                },
              },
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              // disablePortal: false,
            }}
            {...slotProps?.select}
            inputProps={{ id: labelId, ...slotProps?.select?.inputProps }}
          >
            {renderTreeOptions(
              options,
              field,
              checkbox,
              slotProps,
              expandedNodes,
              toggleNode
            )}
          </Select>

          {(!!error || helperText) && (
            <FormHelperText error={!!error} {...slotProps?.formHelperText}>
              {error ? error?.message : helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}

function renderTreeOptions(
  options,
  field,
  checkbox,
  slotProps,
  expandedNodes,
  toggleNode,
  level = 0
) {
  return options.map((option) => {

    const isChecked = (field.value || []).includes(option.id) ||
      (option.children && option.children.length > 0 && option.children.every((child) => (field.value || []).includes(child.id)));

    const isIndeterminate =
      option.children &&
      option.children.some((child) => (field.value || []).includes(child.id)) &&
      !option.children.every((child) => (field.value || []).includes(child.id));

    const handleToggle = (id) => {
      const currentValue = field.value || [];
      const isParent = option.children && option.children.length > 0;

      let newValue;

      if (isParent) {
        // Handle parent toggling
        const allChildIDs = option.children.map((child) => child.id);
        if (currentValue.includes(id)) {
          // Deselect parent and all children
          newValue = currentValue.filter(
            (selectedId) => selectedId !== id && !allChildIDs.includes(selectedId)
          );
        } else {
          // Select parent and all children
          newValue = [...new Set([...currentValue, id, ...allChildIDs])];
        }
        // Handle child toggling
      } else if (currentValue.includes(id)) {
        // Deselect only the child
        newValue = currentValue.filter((selectedId) => selectedId !== id && selectedId !== option.parentID);
      } else {
        // Select only the child
        newValue = [...currentValue, id];
      }

      const isAllSelected = options.every(opt => newValue.includes(opt.id))
      // Update state

      if (isAllSelected && option.parentID) {
        field.onChange([...newValue, option.parentID])
      } else {
        field.onChange(newValue);
      }
    };


    return (
      <div key={option.id}>
        <MenuItem value={option.id}>
          <Box sx={{ width: 24, height: 24 }} >
            {option.children && option.children.length > 0 && (
              <IconButton
                onClick={() => toggleNode(option.id)}
                sx={{ p: 0 }}
              >
                <Iconify icon={expandedNodes[option.id] ? 'material-symbols:expand-less' : 'material-symbols:expand-more'} />
              </IconButton>
            )}
          </Box>
          {checkbox && (
            <Checkbox
              size="small"
              disableRipple
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={() => handleToggle(option.id)}
              {...slotProps?.checkbox}
            />
          )}
          <ListItemText primary={option.name} />
        </MenuItem>
        {option.children && option.children.length > 0 && (
          <Collapse in={expandedNodes[option.id]} timeout="auto" unmountOnExit style={{ paddingLeft: '20px', width: '100%' }}>
            {renderTreeOptions(
              option.children,
              field,
              checkbox,
              slotProps,
              expandedNodes,
              toggleNode,
              level + 1,
            )}
          </Collapse>
        )}
      </div>
    );
  });
}

// Helper function to flatten options tree
const flattenOptions = (option, parentID = null) => {
  const flattened = [{ id: option.id, name: option.name, parentID }];
  if (option.children) {
    option.children.forEach((child) =>
      flattened.push(...flattenOptions(child, option.id))
    );
  }
  return flattened;
};
